import { isAfter, subHours } from 'date-fns';

import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { ignoreTimezone } from '@order/utils';

import type { useLastOrderStatus, useMenuReorderRailOrders } from '../../../hooks';

/**
 * There is a new requirement to show if there are multiple pending orders on the home screen.
 * To avoid querying the order status for the past N orders, this function approximates it.
 * Considers pending if its wanted time is within the last hour and it's not canceled in the db.
 */
export function isPendingOrder(
  order: ReturnType<typeof useMenuReorderRailOrders>['orders'][number],
) {
  if (order.isCanceled || !order?.wantedTime) return false;

  const orderTime = ignoreTimezone(order.wantedTime);

  if (!orderTime) return false;

  const oneHourAgo = subHours(new Date(), 1);
  return isAfter(orderTime, oneHourAgo);
}

/**
 * Only show the rate order button if the order is pending feedback and its wanted time is older than one hour.
 */
export function isPendingFeedbackOrder(
  order: ReturnType<typeof useMenuReorderRailOrders>['orders'][number],
) {
  if (order.isCanceled || !order?.wantedTime) return false;

  const orderTime = ignoreTimezone(order.wantedTime);

  if (!orderTime) return false;

  const oneHourAgo = subHours(new Date(), 1);
  if (isAfter(orderTime, oneHourAgo)) return false;

  return order.isPendingFeedback;
}

export function isPendingOrderStatus(
  orderStatus: ReturnType<typeof useLastOrderStatus>['orderStatus'],
) {
  return orderStatus ? PENDING_ORDER_STATUSES.has(orderStatus) : false;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PENDING_ORDER_STATUSES = new Set<FlattenedOrderStatuses>([
  FlattenedOrderStatuses.Received,
  FlattenedOrderStatuses.Preparing,
  FlattenedOrderStatuses.ReadyForCourier,
  FlattenedOrderStatuses.Delivering,
]);
